import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogIn, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) navigate("/admin");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      <aside className="hidden lg:flex flex-col justify-between p-14 border-r border-foreground">
        <div>
          <p className="label-xs">Portfolio CMS</p>
          <p className="mt-2 font-heading font-black text-5xl tracking-tighter">Admin</p>
        </div>
        <blockquote className="max-w-md">
          <p className="font-heading text-3xl leading-tight tracking-tight">
            "The boring parts are the product."
          </p>
          <p className="mt-4 label-xs">— every senior engineer, eventually</p>
        </blockquote>
        <p className="text-xs font-mono text-muted-foreground">
          Signed sessions expire in 8h. Failed attempts throttled after 5 tries.
        </p>
      </aside>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="flex items-center gap-2 label-xs"><Lock className="h-3 w-3" /> Restricted area</div>
          <h1 className="mt-3 font-heading font-black text-4xl tracking-tighter">Sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use your admin credentials.
          </p>

          <div className="mt-8 space-y-6">
            <label className="block">
              <span className="label-xs">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="admin-email"
                className="mt-2 w-full bg-transparent border-b border-foreground px-0 py-3 font-mono focus:outline-none focus:border-[var(--brand-accent)] transition-colors"
              />
            </label>
            <label className="block">
              <span className="label-xs">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="mt-2 w-full bg-transparent border-b border-foreground px-0 py-3 font-mono focus:outline-none focus:border-[var(--brand-accent)] transition-colors"
              />
            </label>
          </div>

          {error && (
            <p className="mt-4 text-sm text-destructive font-mono" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full cta-solid justify-center disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"} <LogIn className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
